import { menuGroupAnchor } from '../tours.utils'

const EVENT_PAGE = 'eventPage'
const CHECKIN = 'checkIn'

/** An entry line of a group bubble: the exact menu row label (its own namespace) + a short plain explanation. */
const entry = (label, text, ns = EVENT_PAGE) => ({ label, ns, text })

/**
 * One step per side-menu group: the group opens (menu hold), the spotlight covers the row and its
 * entries (the anchor sits on the `li`), the bubble sits right of it — beside the unfolded rail or
 * the open drawer — and only on a phone (< 700 px) under the entries (`bottomLeft`). `description`
 * is a one-line intro, `entries` one line per menu row.
 */
const groupStep = (key, title, description, entries = [], extra = {}) => ({
    key: key.toLowerCase().replace(/[^a-z0-9]+/g, '-') || 'event-page',
    target: `[data-tour="${menuGroupAnchor(key)}"]`,
    placement: 'right',
    placementNarrow: 'bottomLeft',
    menu: { hold: true, group: key },
    title,
    description,
    entries,
    ...extra,
})

/**
 * The dashboard tour (William, 28 Oct 2026, revised twice the same day): a walk through the
 * organizer side menu, one step per group, each one opening the group and saying in plain words
 * what its rows are for — never the content of the event page itself, no jargon (the organizers
 * discover the product from scratch). A group the organizer does not have (Spectators, Live
 * Judging, Qualification…) has no anchor, so its step drops out by itself.
 * Bump `version` when the steps change enough to be worth a replay for everyone.
 */
export const DASHBOARD_TOUR = {
    id: 'dashboard',
    version: 1,
    steps: [
        {
            key: 'welcome',
            target: null,
            title: 'Welcome to your new dashboard',
            description: 'One minute to find your way around the new organizer interface.',
        },
        groupStep('', 'Event page', 'The page athletes and the public will see. You edit it right on the page.', [], {
            menu: { hold: true },
        }),
        groupStep('Athletes', 'Athletes', 'Everything about your athletes’ registration.', [
            entry('Settings', 'categories, prices, spots and registration dates.'),
            entry('Registrations', 'the list of registered athletes and their payments.'),
            entry('Registration tracking', 'how many athletes registered in each category, updated live.'),
        ]),
        groupStep('Volunteers', 'Volunteers', 'The volunteers who help you on the day of the event.', [
            entry('Settings', 'positions to fill and application dates.'),
            entry('Registrations', 'the applications received.'),
            entry('Invite a judge', 'give judge access to someone.', CHECKIN),
        ]),
        groupStep('Spectators', 'Spectators', 'Ticketing for the public.', [
            entry('Settings', 'tickets, prices and sale dates.'),
            entry('Registrations', 'the tickets sold.'),
        ]),
        groupStep('Emailing', 'Emailing', 'Write to your participants.', [
            entry('Send an email', 'to the athletes, volunteers or spectators of your choice.'),
            entry('History', 'the e-mails already sent.'),
        ]),
        groupStep('Planning', 'Planning', 'The order in which athletes compete.', [
            entry('Settings', 'schedule, waves of athletes and judges.'),
            entry('Stream view', 'the planning shown on a screen.'),
        ]),
        groupStep('Live Judging', 'Live Judging', 'Live judging on a tablet or a phone.', [
            entry('Settings', 'turn live judging on and set it up.'),
            entry(
                'The other rows',
                'the screens used on the day: head judge, speaker, screen control, judges and public display.',
                'tours',
            ),
        ]),
        groupStep('Leaderboard', 'Leaderboard', 'Scores and ranking.', [
            entry('Settings', 'enter the scores and manage the ranking.'),
            entry('TV View', 'the ranking shown on a screen.'),
            entry('Claims', 'the disputes sent by athletes.'),
        ]),
        groupStep('Qualification', 'Qualification', 'For an event that qualifies athletes for a next one.', [
            entry('Qualified list', 'the athletes qualified for the next stage.'),
        ]),
        groupStep('Check-in', 'Check-in', 'Welcoming everyone on the day of the event.', [
            entry('Check-in', 'tick off athletes, volunteers and spectators as they arrive.', 'tours'),
            entry('Invite a check-in attendant', 'hand the check-in over to someone.', CHECKIN),
        ]),
        groupStep('Media', 'Media', 'Your event’s photographers.', [
            entry('Invite media', 'give them access to the gallery to publish their photos.', CHECKIN),
        ]),
        groupStep('contracts', 'Contracts', 'The documents your participants must sign.', [
            entry('Settings', 'write the athlete or volunteer contract and turn it on.'),
            entry('Reports', 'who signed, who has not yet.'),
        ]),
        groupStep('reviews', 'Reviews', 'The reviews left by participants after the event.', [], {
            menu: { hold: true },
        }),
        {
            key: 'pin',
            target: '[data-tour="menu-pin"]',
            placement: 'right',
            desktopOnly: true,
            assumeTarget: true,
            menu: { hold: true },
            title: 'Keep the menu open',
            description:
                'Click the pin to keep the menu always open. Otherwise it shrinks to its icons and opens when you hover it.',
        },
        {
            key: 'replay',
            target: '[data-tour="tour-help"]',
            placement: 'right',
            placementNarrow: 'topLeft',
            menu: { hold: true },
            title: 'Replay anytime',
            description: 'You can replay this tour anytime from this button.',
        },
    ],
}

export default DASHBOARD_TOUR
